import { ed25519 } from '@noble/curves/ed25519';
import { sha256 } from '@noble/hashes/sha256';
import { hkdf } from '@noble/hashes/hkdf';
import { concatBytes } from '@noble/hashes/utils';
import type { CeremonyAdapter, RegisterOptions, RegisterResult, LoginOptions, LoginResult } from './ceremony';

/** base64url encode without padding. */
function b64u(bytes: Uint8Array): string {
  return Buffer.from(bytes).toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Inverse of `b64u`. */
function unb64u(value: string): Uint8Array {
  return new Uint8Array(Buffer.from(value.replace(/-/g, '+').replace(/_/g, '/'), 'base64'));
}

interface StoredCredential {
  credentialId: string;
  edSeed: Uint8Array;
  prfSecret: Uint8Array;
}

/**
 * Somewhere a `SoftAuthenticator`'s credentials can outlive the JS context
 * that created them. Two strings in, two strings out — the authenticator owns
 * the encoding, so a store cannot corrupt a credential by mis-serialising it.
 *
 * Exists because a real platform authenticator is shared by every window on
 * the origin, and the in-memory Map is not: a credential created in a
 * registration popup was invisible to the iframe that then had to re-derive
 * from it, and to the step-up popup opened later. That gap silently disabled
 * e2e coverage of the whole registration path (CEF-AI/wallet#91).
 */
export interface SoftCredentialStore {
  read(): string | null;
  write(value: string): void;
}

/**
 * `localStorage`-backed store, for the e2e SPA build only.
 *
 * Per-test isolation comes from Playwright, which gives every test its own
 * BrowserContext and therefore its own `localStorage` — while a popup opened
 * BY the page shares that context, which is exactly the sharing this exists
 * for. Nothing here needs to clear between tests; if a spec ever drives two
 * contexts and wants them to share, that is a different mechanism.
 *
 * Returns a store that silently no-ops when `localStorage` is unreachable
 * (a sandboxed frame, a browser with site data blocked): the authenticator
 * then behaves exactly as it did before this existed — per-context memory —
 * rather than failing the ceremony outright.
 */
export function browserCredentialStore(key = 'scp-wallet-e2e:soft-authenticator'): SoftCredentialStore {
  return {
    read() {
      try {
        return globalThis.localStorage?.getItem(key) ?? null;
      } catch {
        return null;
      }
    },
    write(value) {
      try {
        globalThis.localStorage?.setItem(key, value);
      } catch {
        /* no storage: degrade to per-context memory */
      }
    },
  };
}

/** The JSON shape `SoftCredentialStore` carries. Internal to this module. */
interface SerialisedState {
  regCounter: number;
  creds: Array<{ credentialId: string; edSeed: string; prfSecret: string }>;
}

export interface SoftAuthenticatorOptions {
  /**
   * If provided, derive the per-registration `edSeed`, `prfSecret`, and
   * `credentialId` deterministically from this seed (mixed with a monotonic
   * counter so multiple register() calls on the same instance still produce
   * distinct credentials). Used by Playwright e2e specs that pin server-side
   * address stubs against the SoftAuthenticator's output. Production code
   * never sets this — the `useSoft` branch in `createIdentity.ts` is itself
   * forbidden in production builds.
   *
   * Default: undefined (each register() draws fresh CSPRNG bytes, matching
   * the pre-cycle-8.1 behavior used by unit tests in `ceremony.test.ts`).
   */
  seed?: string;

  /**
   * When the caller invokes login() without a credentialId, fall back to the
   * most-recently-registered credential in memory. Default: false (login()
   * throws when credentialId is missing, matching pre-cycle-8 behavior).
   *
   * Required for e2e flows that simulate the user signing back in after the
   * vault has been cleared (post-logout re-login): the wallet has discarded
   * the credentialId locally, but the soft authenticator's in-memory creds
   * map still holds the registration, and the spec test needs login() to
   * succeed anyway. Production code must never enable this — real WebAuthn's
   * no-allowCredentials path shows an OS credential picker; the fallback
   * here is a deterministic test convenience, not a real picker, so leaving
   * it on by default would silently let a single registered credential
   * impersonate any login.
   */
  fallbackToLastRegistered?: boolean;

  /**
   * Where credentials live. Omitted (the default) they live in this instance's
   * memory, which is what every unit test wants and what this class always
   * did. The e2e SPA build passes `browserCredentialStore()` so that a
   * credential registered in a popup is visible to the iframe and to later
   * popups — as a real platform authenticator's would be.
   *
   * The registration counter is stored alongside the credentials, so a second
   * window registering against the same seed produces the NEXT credential
   * rather than colliding with the first.
   */
  store?: SoftCredentialStore;
}

/**
 * Deterministic in-memory WebAuthn authenticator for tests. NOT for production.
 *
 * - Stores credentials in a private Map keyed by credentialId.
 * - `prfOutput` is computed as HKDF-SHA256(prfSecret, salt="", info=prfInput, L=32),
 *   so for a given credential and prfInput the result is stable across login calls.
 * - `signature` is a valid Ed25519 signature over `authenticatorData || sha256(clientDataJSON)`.
 *
 * The output shapes match the real WebAuthn response well enough that the
 * `Identity` integration tests (Task 6) can drive register → login → derive
 * → cross-check without a real browser.
 */
export class SoftAuthenticator implements CeremonyAdapter {
  private readonly creds: Map<string, StoredCredential> = new Map();
  private readonly seedBytes: Uint8Array | null;
  private readonly fallbackToLastRegistered: boolean;
  private readonly store: SoftCredentialStore | null;
  /** Monotonic counter mixed into the seed-derived material so successive
   *  register() calls on a seeded instance produce distinct credentials. */
  private regCounter = 0;

  constructor(opts: SoftAuthenticatorOptions = {}) {
    this.seedBytes = opts.seed != null ? new TextEncoder().encode(opts.seed) : null;
    this.fallbackToLastRegistered = opts.fallbackToLastRegistered === true;
    this.store = opts.store ?? null;
  }

  /**
   * Pull the shared store into this instance's Map before any read of it.
   *
   * Re-read every time rather than once at construction: the other window may
   * have registered since. Merges rather than replaces, so a credential this
   * instance created is never lost to a stale write from elsewhere — the
   * store is last-writer-wins per key, and these keys are content-addressed
   * (a credentialId is derived from the material it names), so a merge cannot
   * produce a credential whose id disagrees with its bytes.
   */
  private hydrate(): void {
    if (!this.store) return;
    let state: SerialisedState;
    try {
      const raw = this.store.read();
      if (!raw) return;
      state = JSON.parse(raw) as SerialisedState;
    } catch {
      // A store that throws (site data blocked, a sandboxed frame) or holds
      // corrupt JSON is a harness problem, not a product one. Fall back to
      // per-context memory — the behaviour before this store existed — rather
      // than failing a WebAuthn ceremony, which would point every reader at
      // entirely the wrong layer.
      return;
    }
    for (const c of state.creds ?? []) {
      if (this.creds.has(c.credentialId)) continue;
      this.creds.set(c.credentialId, {
        credentialId: c.credentialId,
        edSeed: unb64u(c.edSeed),
        prfSecret: unb64u(c.prfSecret),
      });
    }
    // The counter is shared state too: two windows seeded identically must not
    // both mint `reg-0`.
    if (typeof state.regCounter === 'number' && state.regCounter > this.regCounter) {
      this.regCounter = state.regCounter;
    }
  }

  /** Publish this instance's credentials + counter back to the shared store. */
  private persist(): void {
    if (!this.store) return;
    const state: SerialisedState = {
      regCounter: this.regCounter,
      creds: Array.from(this.creds.values()).map((c) => ({
        credentialId: c.credentialId,
        edSeed: b64u(c.edSeed),
        prfSecret: b64u(c.prfSecret),
      })),
    };
    try {
      this.store.write(JSON.stringify(state));
    } catch {
      // Same reasoning as `hydrate`: an unwritable store degrades this
      // instance to memory, it does not break the ceremony in progress.
    }
  }

  async register(opts: RegisterOptions): Promise<RegisterResult> {
    // Before minting: another window may already have used this counter value.
    this.hydrate();
    // Generate per-credential material. When seeded, derive deterministically
    // via HKDF-SHA256(seed, salt=<counter>, info=<purpose>) so the e2e specs
    // can pin server-side address stubs against the resulting addresses.
    let edSeed: Uint8Array;
    let prfSecret: Uint8Array;
    let credentialIdBytes: Uint8Array;
    if (this.seedBytes) {
      const salt = new TextEncoder().encode(`reg-${this.regCounter++}`);
      edSeed = hkdf(sha256, this.seedBytes, salt, 'ed-seed', 32);
      prfSecret = hkdf(sha256, this.seedBytes, salt, 'prf-secret', 32);
      credentialIdBytes = hkdf(sha256, this.seedBytes, salt, 'credential-id', 16);
    } else {
      edSeed = ed25519.utils.randomPrivateKey();
      prfSecret = ed25519.utils.randomPrivateKey(); // 32 random bytes
      credentialIdBytes = ed25519.utils.randomPrivateKey().slice(0, 16); // 16-byte id
    }
    const credentialId = b64u(credentialIdBytes);

    this.creds.set(credentialId, { credentialId, edSeed, prfSecret });
    this.persist();

    // Compose the WebAuthn-shaped artifacts. These are NOT spec-correct attestations;
    // they're test fixtures whose only requirement is to round-trip through identity.
    const clientData = {
      type: 'webauthn.create',
      challenge: b64u(opts.challenge),
      origin: `https://${opts.rpId}`,
      crossOrigin: false,
    };
    const clientDataJSON = new TextEncoder().encode(JSON.stringify(clientData));
    // Not a spec-correct attestation — but it carries the credential id, the
    // one field a real attestation has that a server actually needs. A real
    // `register/finish` reads the id out of the authenticator data inside the
    // attestation object; the client never sends it separately. A stub server
    // that invents its own id instead leaves the frame pinning
    // `allowCredentials` to something no authenticator knows, which is what
    // silently disabled every e2e of the post-registration re-derivation
    // (CEF-AI/wallet#91). See `credentialIdFromAttestation` in the e2e harness.
    const attestationObject = new TextEncoder().encode(JSON.stringify({ credentialId }));

    const prfOutput = this.computePrf(prfSecret, opts.prfInput);

    return {
      credentialId,
      clientDataJSON: b64u(clientDataJSON),
      attestationObject: b64u(attestationObject),
      transports: ['internal'],
      prfOutput,
    };
  }

  async login(opts: LoginOptions): Promise<LoginResult> {
    // Two modes:
    // 1. Explicit credentialId (the common path, including IdentityImpl's
    //    warm-vault re-mint flow): look it up and throw on miss.
    // 2. No credentialId: by default this is an error (pre-cycle-8 behaviour),
    //    because a silent "pick the last one" is a security trap if it ever
    //    leaks into production. Callers that genuinely need the fallback
    //    (Playwright post-logout re-login) opt in via
    //    `new SoftAuthenticator({ fallbackToLastRegistered: true })`.
    // The credential may have been registered in another window — the popup
    // that ran `register()` while this is the iframe re-deriving from it, or a
    // popup that has since closed.
    this.hydrate();
    let stored: StoredCredential | undefined;
    if (opts.credentialId) {
      stored = this.creds.get(opts.credentialId);
      if (!stored) {
        throw new Error(`SoftAuthenticator.login: unknown credential ${opts.credentialId}`);
      }
    } else {
      if (!this.fallbackToLastRegistered) {
        throw new Error(
          'SoftAuthenticator.login: credentialId is required ' +
            '(set `fallbackToLastRegistered: true` in the constructor to opt into the e2e convenience of picking the most-recently-registered credential)',
        );
      }
      const all = Array.from(this.creds.values());
      stored = all[all.length - 1];
      if (!stored) {
        throw new Error('SoftAuthenticator.login: no credentials registered');
      }
    }

    const clientData = {
      type: 'webauthn.get',
      challenge: b64u(opts.challenge),
      origin: `https://${opts.rpId}`,
      crossOrigin: false,
    };
    const clientDataJSON = new TextEncoder().encode(JSON.stringify(clientData));

    // Minimal authenticatorData: 32-byte rpIdHash || 1-byte flags || 4-byte signCount.
    // We don't need a real rpIdHash for client-side tests.
    const authenticatorData = new Uint8Array(37);
    authenticatorData[32] = 0x05; // UP + UV flags

    const clientDataHash = sha256(clientDataJSON);
    const toSign = concatBytes(authenticatorData, clientDataHash);
    const signature = ed25519.sign(toSign, stored.edSeed);

    const prfOutput = this.computePrf(stored.prfSecret, opts.prfInput);

    return {
      credentialId: stored.credentialId,
      clientDataJSON: b64u(clientDataJSON),
      authenticatorData: b64u(authenticatorData),
      signature: b64u(signature),
      prfOutput,
    };
  }

  /** Public-key bytes for a credential. Used by integration tests for cross-checks. */
  getEd25519PublicKey(credentialId: string): Uint8Array {
    this.hydrate();
    const stored = this.creds.get(credentialId);
    if (!stored) throw new Error(`unknown credential ${credentialId}`);
    return ed25519.getPublicKey(stored.edSeed);
  }

  private computePrf(prfSecret: Uint8Array, prfInput: Uint8Array): Uint8Array {
    // Deterministic per (prfSecret, prfInput) — matches the spirit of how a real
    // authenticator's PRF extension works (HKDF-style mixing of a per-credential
    // secret with the caller's input). NOT identical to any real authenticator.
    return hkdf(sha256, prfSecret, new Uint8Array(0), prfInput, 32);
  }
}
